/**
 * @fileoverview QR code generation utility for registration codes
 * 
 * @purpose Generate QR code data URLs or SVG strings for registration code links
 * @usage Server-side utility for generating QR codes in admin UI or API routes
 * @ownership Core utilities
 * @dependencies
 * - qrcode
 * @restrictions
 * - Server-side only (uses Node.js crypto)
 */

import QRCode from 'qrcode'

export type QRCodeOptions = {
  size?: number
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H'
  margin?: number
}

/**
 * Generate QR code as data URL (PNG)
 * 
 * @param url - URL to encode in QR code
 * @param options - QR code generation options
 * @returns Data URL string (e.g., "data:image/png;base64,...")
 */
export async function generateQRCodeDataURL(
  url: string,
  options: QRCodeOptions = {}
): Promise<string> {
  const {
    size = 200,
    errorCorrectionLevel = 'M',
    margin = 1,
  } = options

  try {
    const dataURL = await QRCode.toDataURL(url, {
      width: size,
      errorCorrectionLevel,
      margin,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
    })
    return dataURL
  } catch (error) {
    throw new Error(`Failed to generate QR code: ${error instanceof Error ? (error instanceof Error ? (error instanceof Error ? error.message : String(error)) : String(error)) : String(error)}`)
  }
}

/**
 * Generate QR code as SVG string
 * 
 * @param url - URL to encode in QR code
 * @param options - QR code generation options
 * @returns SVG string
 */
export async function generateQRCodeSVG(
  url: string,
  options: QRCodeOptions = {}
): Promise<string> {
  const {
    size = 200,
    errorCorrectionLevel = 'M',
    margin = 1,
  } = options

  try {
    const svg = await QRCode.toString(url, {
      type: 'svg',
      width: size,
      errorCorrectionLevel,
      margin,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
    })
    return svg
  } catch (error) {
    throw new Error(`Failed to generate QR code SVG: ${error instanceof Error ? (error instanceof Error ? (error instanceof Error ? error.message : String(error)) : String(error)) : String(error)}`)
  }
}

/**
 * Generate QR code for registration code link
 * 
 * @param code - Registration code
 * @param baseUrl - Base URL of the application
 * @param options - QR code generation options
 * @returns Data URL string
 */
export async function generateRegistrationCodeQRCode(
  code: string,
  baseUrl: string,
  options: QRCodeOptions = {}
): Promise<string> {
  const registrationUrl = `${baseUrl}/auth/register?code=${encodeURIComponent(code)}`
  return generateQRCodeDataURL(registrationUrl, options)
}
